import { ref, computed, type Ref } from "vue"
import { apiClient } from "@/api/client"
import { db, type FocusFlowDB } from "@/db"
import type { Table } from "dexie"

/*
 * Factory that produces a Pinia setup function for a single REST resource.
 *
 * Usage:
 *   export const useEventsStore = defineStore("events", () =>
 *     createResourceStore<Event>({
 *       resource: "events",
 *       envelopeSingular: "event",
 *       envelopePlural: "events",
 *       dexieTable: "events",
 *     }),
 *   )
 *
 * Each store gets fetch / create / update / destroy / customAction
 * with optimistic UI + Dexie mirror + offline fallback baked in.
 */
export interface ResourceConfig {
  resource: string
  envelopeSingular: string
  envelopePlural: string
  dexieTable: keyof FocusFlowDB
}

export interface ResourceRecord {
  id: number
}

export function createResourceStore<T extends ResourceRecord>(cfg: ResourceConfig) {
  const items = ref<T[]>([]) as Ref<T[]>
  const loading = ref(false)
  const error = ref<string | null>(null)
  const table = db[cfg.dexieTable] as unknown as Table<Record<string, unknown>, number>

  const all = computed(() => items.value)
  const count = computed(() => items.value.length)

  async function fetch(params: Record<string, unknown> = {}): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const { data } = await apiClient.get(`/${cfg.resource}`, { params })
      const list = (data[cfg.envelopePlural] || []) as T[]
      items.value = list
      await table.bulkPut(list as unknown as Record<string, unknown>[])
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : `Failed to load ${cfg.resource}`
      error.value = message
      const cached = await table.toArray()
      if (cached.length > 0) items.value = cached as unknown as T[]
    } finally {
      loading.value = false
    }
  }

  async function fetchOne(id: number): Promise<T | null> {
    try {
      const { data } = await apiClient.get(`/${cfg.resource}/${id}`)
      const record = data[cfg.envelopeSingular] as T
      await table.put(record as unknown as Record<string, unknown>)
      const idx = items.value.findIndex((x) => x.id === id)
      if (idx >= 0) items.value[idx] = record
      else items.value.push(record)
      return record
    } catch {
      const cached = await table.get(id)
      return (cached as unknown as T) ?? null
    }
  }

  async function create(attrs: Record<string, unknown>): Promise<T> {
    const body = { [cfg.envelopeSingular]: attrs }
    const { data } = await apiClient.post(`/${cfg.resource}`, body)
    const record = data[cfg.envelopeSingular] as T
    items.value = [record, ...items.value]
    await table.put(record as unknown as Record<string, unknown>)
    return record
  }

  async function update(id: number, attrs: Record<string, unknown>): Promise<T> {
    const idx = items.value.findIndex((x) => x.id === id)
    const previous = idx >= 0 ? { ...items.value[idx] } : null
    if (idx >= 0) {
      items.value[idx] = { ...items.value[idx], ...attrs } as T
    }
    try {
      const body = { [cfg.envelopeSingular]: attrs }
      const { data } = await apiClient.patch(`/${cfg.resource}/${id}`, body)
      const record = data[cfg.envelopeSingular] as T
      if (idx >= 0) items.value[idx] = record
      await table.put(record as unknown as Record<string, unknown>)
      return record
    } catch (e) {
      if (idx >= 0 && previous) items.value[idx] = previous as T
      throw e
    }
  }

  async function destroy(id: number): Promise<void> {
    const previous = [...items.value]
    items.value = items.value.filter((x) => x.id !== id)
    try {
      await apiClient.delete(`/${cfg.resource}/${id}`)
      await table.delete(id)
    } catch (e) {
      items.value = previous
      throw e
    }
  }

  /* Custom action helper: PATCH /resource/:id/:action returning the
   * envelope-singular record. Used for tasks#complete, projects#park,
   * reminders#dismiss, alerts#snooze, etc. */
  async function customAction(id: number, action: string, payload?: Record<string, unknown>): Promise<T> {
    const idx = items.value.findIndex((x) => x.id === id)
    const previous = idx >= 0 ? { ...items.value[idx] } : null
    try {
      const { data } = await apiClient.patch(`/${cfg.resource}/${id}/${action}`, payload || {})
      const record = data[cfg.envelopeSingular] as T
      if (idx >= 0) items.value[idx] = record
      await table.put(record as unknown as Record<string, unknown>)
      return record
    } catch (e) {
      if (idx >= 0 && previous) items.value[idx] = previous as T
      throw e
    }
  }

  return {
    items, loading, error,
    all, count,
    fetch, fetchOne, create, update, destroy, customAction,
  }
}
